// Package imports:
import React, { useMemo, useRef } from "react";
import { Formik } from "formik";
import Bugsnag from "@bugsnag/js";
import cx from 'classnames';
// Component imports:
import Modal from "../../../ui-elements/Modal/Modal";
import { AddIndividualForm, validate } from "./AddIndividualForm";
import Link from '../../../ui-elements/Link/Link';
import FileUploadComponent, { ValidationError } from '../../../ui-elements/ImportButton/ImportButton';
import Table from '../../../ui-elements/Table/Table';
import Button from '../../../ui-elements/Button/Button';
import ProgressBar from '../../../ui-elements/ProgressBar/ProgressBar';
import Alert from '../../../ui-elements/Alert/Alert';
// Service imports:
import { ErrorMessages } from "../../../services/errorMessages";
import { insertDash } from '../../../services/utils';
// Type imports:
import {
    AddIndividualFormValues,
    IDueDiligenceModalProps,
    KnowYourIndividual,
    ServiceCompanyEmployee,
    AddMultipleCustomersFeedback,
    BatchOperation,
    BatchOperationResult
} from "../../../types/DueDiligenceTypes";
import { UploadedEntity } from "../AddCompanyModal/AddCompanyModal";


// Props for the AddIndividualModal component
interface IProps extends IDueDiligenceModalProps {
    employees: ServiceCompanyEmployee[]; // List of employees for supervisor dropdown
    onAddIndividual: (individual: KnowYourIndividual) => void; // Callback when individual is successfully added
    onUpdateIndividual: (individualId: number, updates: Partial<KnowYourIndividual>) => void; // Callback when individual is updated
}

// Minimum number of errors to show before requiring user to expand
const MIN_VISIBLE_ERRORS = 6;

// Interface to track upload success state
interface IUploadSuccess {
    uploadSuccess: boolean | null; // null = not attempted, true = success, false = failure
    uploadedCount: number; // Number of individuals successfully uploaded
}

const AddIndividualModal: React.FC<IProps> = ({
    show,
    close,
    setToastShow,
    setToastType,
    setToastMessage,
    employees,
    onAddIndividual,
    onUpdateIndividual
}) => {
    // State to disable submit buttons during processing
    const [disableSubmit, setDisableSubmit] = React.useState(false);

    // Track the current state of file parsing/submission
    // idle = no file uploaded, parsing = processing file, completed = ready to submit, submitting = sending to server
    const [parsingState, setParsingState] = React.useState<"idle" | "parsing" | "completed" | "submitting">("idle");

    // Store the parsed entities from the uploaded file
    const [uploadedEntities, setUploadedEntities] = React.useState<UploadedEntity[]>([]);

    // Track progress during file parsing and submission
    const [progress, setProgress] = React.useState<{ processedRows: number; totalRows: number }>({
        processedRows: 0,
        totalRows: 0
    });

    // Store validation errors from file parsing or submission
    const [errors, setErrors] = React.useState<ValidationError[]>([]);

    // Reference to the uploaded file for submission
    const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);

    // Control how many error messages are visible (can be expanded by user)
    const [visibleErrorCount, setVisibleErrorCount] = React.useState(MIN_VISIBLE_ERRORS);

    // Track the final result of the upload operation
    const [uploadSuccess, setUploadSuccess] = React.useState<IUploadSuccess>({
        uploadSuccess: null,
        uploadedCount: 0
    });

    /**
     * Closes the modal and resets all state to initial values
     * This ensures a clean slate when reopening the modal
     */
    const handleClose = () => {
        setParsingState("idle");
        setErrors([]);
        setUploadedFile(null);
        setProgress({ processedRows: 0, totalRows: 0 });
        setDisableSubmit(false);
        setUploadedEntities([]);
        setUploadSuccess({ uploadSuccess: null, uploadedCount: 0 });
        setVisibleErrorCount(MIN_VISIBLE_ERRORS);
        close();
    }

    // Callback when file upload processing begins
    const onProcessingStart = () => {
        setDisableSubmit(true);
        setParsingState("parsing");
    }

    // Callback to update progress bar during file parsing
    const onProgressUpdate = (processedRows: number, totalRows: number) => {
        setProgress({ processedRows, totalRows });
    }

    // Callback when file parsing completes successfully
    const onProcessingComplete = () => {
        setDisableSubmit(false);
        setParsingState("completed");
    }

    /**
     * Handles form submission for adding a single individual
     * This is the standard form submission path (not bulk upload)
     */
    const handleSubmit = async (values: AddIndividualFormValues) => {
        try {
            setDisableSubmit(true);

            // Prepare payload - format dateOfBirth as ISO string for DateTime in .NET
            const payload = {
                ...values,
                dateOfBirth: values.dateOfBirth ? new Date(values.dateOfBirth).toISOString() : null
            };

            // Send the individual data to the backend API
            const response = await fetch('/DueDiligence/AddNewIndividual', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            setDisableSubmit(false);

            // Handle error responses from the API
            if (!response.ok) {
                const errorData = await response.text();
                setToastType("error");
                setToastMessage(errorData || 'Ekki tókst að bæta við einstakling');
                setToastShow(true);
                return;
            }

            // Successfully added individual - update parent component and show success message
            const newIndividual: KnowYourIndividual = await response.json();
            onAddIndividual(newIndividual);
            setToastType("success");
            setToastMessage(newIndividual.name ? `${newIndividual.name} hefur verið bætt við.` : 'Einstaklingi hefur verið bætt við.');
            setToastShow(true);
            handleClose();

        } catch (err: any) {
            setDisableSubmit(false);
            // Log error to Bugsnag for debugging
            if (err instanceof Error) Bugsnag.notify(err);
            setToastType("error");
            setToastMessage(ErrorMessages.OtherShort);
            setToastShow(true);
        }
    };

    /**
     * Cancels the bulk upload and resets all related state
     * Allows user to start over with a new file
     */
    const cancelUpload = () => {
        setErrors([]);
        setParsingState("idle");
        setUploadedEntities([]);
        setUploadedFile(null);
        setProgress({ processedRows: 0, totalRows: 0 });
        setDisableSubmit(false);
        setVisibleErrorCount(MIN_VISIBLE_ERRORS);
    }

    // Reference to the abort function from FileUploadComponent
    // This allows us to programmatically cancel file processing
    const abortRef = useRef<(() => void) | null>(null);

    /**
     * Called when file processing is aborted by user
     * Resets processing state and clears any partial results
     */
    const onProcessingAborted = () => {
        setParsingState("idle");
        setUploadedFile(null);
        setProgress({ processedRows: 0, totalRows: 0 });
        setDisableSubmit(false);
        setUploadedEntities([]);
        setErrors([]);
    };

    /**
     * Handles the abort button click during file parsing
     * Triggers the abort function exposed by FileUploadComponent
     */
    const handleAbort = () => {
        if (abortRef.current) {
            abortRef.current();
        }
    };

    // Memoize the form component to prevent unnecessary re-renders
    const subFormComponent = useMemo(() => {
        return (props: { values: AddIndividualFormValues }) => (
            <AddIndividualForm
                values={props.values}
                employees={employees}
                closeModal={handleClose}
                disableSubmit={disableSubmit}
            />
        )
    }, [disableSubmit]);

    /**
     * Fetches the final results of a completed batch operation
     * Called after polling confirms the batch processing is complete
     * 
     * @param batchId - The unique identifier for the batch operation
     * @returns Promise containing the final results with any errors or successfully processed individuals
     */
    const fetchBatchResults = async (batchId: number): Promise<BatchOperation> => {
        try {
            const response = await fetch(`/Batch/GetBatchStatusResults/${batchId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to get batch results: ${response.statusText}`);
            }

            const results: BatchOperation = await response.json();
            return results;

        } catch (error) {
            console.error('Error fetching batch results:', error);
            throw error;
        }
    };

    /**
     * Polls the batch status endpoint to monitor progress of a batch operation
     * Continues checking until the batch is completed or timeout occurs
     * 
     * @param batchId - The unique identifier for the batch operation to monitor
     * @param onProgress - Optional callback to receive real-time progress updates
     */
    const pollBatchStatus = async (
        batchId: number,
        onProgress?: (operation: BatchOperation) => void
    ): Promise<void> => {
        // Maximum polling attempts to prevent infinite loops (5 minutes max)
        const maxAttempts = 300;
        let attempts = 0;

        while (attempts < maxAttempts) {
            try {
                // Query the current status of the batch operation
                const response = await fetch(`/Batch/GetBatchStatus/${batchId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to get batch status: ${response.statusText}`);
                }

                const operation: BatchOperation = await response.json();

                // Notify caller of progress so UI can update
                if (onProgress) {
                    onProgress(operation);
                }

                // Exit polling loop if batch is complete
                if (operation.complete) {
                    return;
                }

                // Use shorter interval for first few attempts, then back off
                const timeoutDuration = attempts < 3 ? 2000 : 5000;
                await new Promise(resolve => setTimeout(resolve, timeoutDuration));

                attempts++;

            } catch (error) {
                console.error('Error polling batch status:', error);
                throw error;
            }
        }

        // Timeout occurred - batch took too long to process
        throw new Error('Batch status polling timed out');
    };

    /**
     * Handles submission of the bulk upload file
     * Sends file to server, polls for completion, and processes results
     */
    const handleSubmitUploadedEntities = async () => {
        try {
            // Initialize submitting state and reset any previous errors
            setParsingState("submitting");
            setErrors([]);
            setProgress({ processedRows: 0, totalRows: uploadedEntities.length });

            // Ensure we have a file to submit
            if (!uploadedFile) {
                console.error('No file to submit');
                return;
            }

            setDisableSubmit(true);

            // Create FormData to send file as multipart/form-data
            const formData = new FormData();
            formData.append('excelFile', uploadedFile);

            // Submit the file to the individuals bulk upload endpoint
            const response = await fetch('/DueDiligence/AddMultipleIndividuals', {
                method: 'POST',
                body: formData // Browser automatically sets correct Content-Type with boundary
            });

            const responseData: AddMultipleCustomersFeedback = await response.json();

            if (response.ok) {
                // File accepted - begin polling for batch completion
                if (responseData.batchId) {
                    await pollBatchStatus(
                        responseData.batchId,
                        (operation) => {
                            // Update progress bar as batch processes
                            setProgress({ processedRows: operation.count, totalRows: operation.totalCount });
                            setTimeout(() => { }, 0); // Force UI update
                        }
                    );

                    // Fetch the final results after batch completes
                    const resultsResponse = await fetch(`/Batch/GetBatchStatusResults/${responseData.batchId}`);

                    if (!resultsResponse.ok) {
                        throw new Error('Failed to get batch results');
                    }

                    const finalResults: BatchOperationResult = await fetchBatchResults(responseData.batchId);

                    // Check if any individuals failed to process
                    const hasErrors = finalResults.items?.filter(item => item.errorMessage);
                    const successfulItems = finalResults.items?.filter(item => !item.errorMessage && item.userId && item.individualId);

                    // Update parent table with successfully added individuals
                    if (successfulItems && successfulItems.length > 0) {
                        successfulItems.forEach(item => {
                            // Find the corresponding uploaded entity using the SSN
                            const uploadedEntity = uploadedEntities.find(entity =>
                                entity.kennitala === item.ssn
                            );

                            if (uploadedEntity && item.ssn) {
                                // Find supervisor info from the uploaded entity's supervisor email/username
                                const supervisor = employees.find(emp =>
                                    emp.email === uploadedEntity.netfangUmsjonaradila ||
                                    emp.userName === uploadedEntity.netfangUmsjonaradila
                                );

                                // Check if this is a partial success (update only) vs full success (new individual)
                                if (item.partialSuccess) {
                                    // Partial success: Individual already exists, only update email and supervisor
                                    // Update the individual in the table with new email and supervisor info
                                    if (item.individualId) {
                                        const updates: Partial<KnowYourIndividual> = {
                                            email: uploadedEntity.netfang || '',
                                            supervisorName: supervisor?.fullname || '',
                                            supervisorSSN: supervisor?.ssn || '',
                                            supervisorId: supervisor?.webUserId || null
                                        };
                                        onUpdateIndividual(item.individualId, updates);
                                    }
                                } else {
                                    // Full success: New individual was added, construct full object
                                    const newIndividual: KnowYourIndividual = {
                                        name: item.userName || '',
                                        ssn: item.ssn,
                                        email: uploadedEntity.netfang || '',
                                        address: '',
                                        areaCode: '',
                                        region: '',
                                        country: '',
                                        nationality: '',
                                        userId: item.userId!,
                                        individualCustomerId: item.individualId!,
                                        supervisorName: supervisor?.fullname || '',
                                        supervisorSSN: supervisor?.ssn || '',
                                        supervisorId: supervisor?.webUserId || null,
                                        questionsAnswered: false,
                                        answeredTimestamp: null,
                                        questionDateSent: null,
                                        riskscoreCreated: null,
                                        riskscoreId: null,
                                        riskscoreDescription: '',
                                        riskscore: '',
                                        status: '',
                                        statusDescription: '',
                                        checked: false
                                    };

                                    onAddIndividual(newIndividual);
                                }
                            }
                        });
                    }

                    if (finalResults.complete && hasErrors && hasErrors.length > 0) {
                        // Some individuals failed - display errors to user
                        setErrors([{
                            row: 0,
                            field: 'Skrá',
                            value: uploadedFile.name,
                            headerMessage: 'Eftirfarandi villur fundust við vinnslu skráar:',
                            message: <div>
                                <div>
                                    {hasErrors.map((err, idx) => {
                                        // Parse error messages that contain "Ástæða:" for better formatting
                                        if (err.errorMessage && err.errorMessage.includes("Ástæða:")) {
                                            const [before, after] = err.errorMessage.split("Ástæða:");
                                            return (
                                                <p className='paragraph-small' key={idx}>
                                                    {before}
                                                    <b>Ástæða:</b>
                                                    {after}
                                                </p>
                                            );
                                        }
                                        return <p className='paragraph-small' key={idx}>{err.errorMessage}</p>;
                                    })}
                                </div>
                            </div>
                        }]);
                    } else if (finalResults.complete && finalResults.items?.length === finalResults.totalCount) {
                        // All individuals processed successfully
                        setUploadSuccess({ uploadSuccess: true, uploadedCount: finalResults.items.length });
                    }
                    else {
                        // Unexpected state - log for debugging
                        Bugsnag.notify(new Error('Unexpected state in batch processing results'));
                        setUploadSuccess({ uploadSuccess: false, uploadedCount: finalResults.items ? finalResults.items.length : 0 });
                    }
                }
            }

            //? Error handling for failed submission
            // Check for template errors (file format issues)
            if (responseData) {
                if (responseData.templateError) {
                    Bugsnag.notify(new Error('Template error when submitting multiple individuals: ' + responseData.errorMessage));
                    setErrors([{
                        row: 0,
                        field: 'Skrá',
                        value: uploadedFile.name,
                        message: 'Villa í sniðmáti skráar. Vinsamlegast notið sniðmátið hér að ofan'
                    }]);
                }
                // Check for immediate validation errors
                else if (responseData.errors && responseData.errors.length > 0) {
                    setErrors([{
                        row: 0,
                        field: 'Skrá',
                        value: uploadedFile.name,
                        headerMessage: 'Eftirfarandi villur fundust við vinnslu skráar:',
                        message: <div>
                            <div>
                                {responseData.errors.map((errorItem, idx) => {
                                    const ssn = errorItem.individualSsn || 'Óþekkt';
                                    const email = errorItem.individualEmail || '';
                                    const rowNum = errorItem.rowNumber;
                                    const errorMessages = errorItem.errors || [];

                                    return (
                                        <div key={idx} className='paragraph-small' style={{ marginBottom: '12px' }}>
                                            <p style={{ marginBottom: '4px' }}>
                                                <b>Röð {rowNum}:</b> {ssn} {email ? `(${email})` : ''}
                                            </p>
                                            <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                                                {errorMessages.map((errMsg, errIdx) => (
                                                    <li key={errIdx}>{errMsg}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    }]);
                }
            }
        }
        catch (err: any) {
            console.error('Error submitting file:', err);
        }
        finally {
            // Always reset state after submission completes
            setParsingState("completed");
            setDisableSubmit(false);
        }
    };

    /**
     * Renders the modal body content based on current state
     * Shows different UI for: success, error, parsing, submitting, or form entry
     */
    const renderBody = () => {
        // Find any file-level errors (not row-specific errors)
        const FILE_ERROR = errors.find(err => err.field === 'Skrá');

        // Count entities with validation errors
        const hasErrors = uploadedEntities.filter(entity => entity.error && entity.error.length > 0);

        // determine if multiple entities were uploaded
        const isPlural = uploadSuccess.uploadedCount > 1;

        // Show success message if upload completed successfully
        if (uploadSuccess.uploadSuccess === true) {
            return <Alert
                type='success'
                headText={"Skránni hefur verið hlaðið upp"}
                text={<>
                    {`${(isPlural ? 'Alls voru' : '')} ${uploadSuccess.uploadedCount} ${(isPlural ? 'einstaklingum' : 'einstaklingi')} bætt við eða breytt.`}
                </>}
            />;
        }
        // Show error message if upload failed
        else if (uploadSuccess.uploadSuccess === false) {
            return <Alert
                type='error'
                headText={"Ekki tókst að hlaða skránni upp"}
                text={`Eitthvað fór úrskeiðis, vinsamlegast reyndu aftur síðar.`}
            />;
        }

        // Show parsing progress
        if (parsingState === "parsing") {
            return <div>
                <p className="paragraph">Unnið er úr skrá, vinsamlegast bíðið...</p>
                <ProgressBar current={progress.processedRows} total={progress.totalRows} />
                <Button buttonType='secondary' onClick={handleAbort}>Hætta við</Button>
            </div>;
        }
        // Show submission progress
        else if (parsingState === "submitting") {
            return <div>
                <p className="paragraph">
                    Uppfæri {(isPlural ? 'einstaklinga' : 'einstakling')}...
                </p>
                <ProgressBar current={progress.processedRows} total={progress.totalRows} />
            </div>;
        }

        /**
         * Renders a table showing all uploaded entities and their validation status
         * Includes error messages and action buttons
         */
        const showUploadedEntities = () => {
            return <div className='KCL_add-customer-modal uploaded-entities-section'>
                <Table
                    tableSize='sm'
                    withPagination="auto"
                    data={uploadedEntities}
                    // Highlight rows with errors in red
                    rowClassName={(row) => row.error?.length === 0 ? '' : 'table__row--error'}
                    columns={[
                        {
                            title: 'Kennitala',
                            renderCell: ({ kennitala }) => insertDash(kennitala),
                            textAlign: 'left',
                        },
                        {
                            title: 'Netfang',
                            renderCell: ({ netfang }) => netfang ? <Link linkSize='13' url={`mailto:${netfang}`}>{netfang}</Link> : '-',
                            textAlign: 'left'
                        },
                        {
                            title: 'Umsjónaraðili',
                            renderCell: ({ netfangUmsjonaradila }) => netfangUmsjonaradila || '-',
                            textAlign: 'left'
                        }
                    ]}
                />

                {/* Display error summary and details if there are validation errors */}
                {hasErrors.length > 0 &&
                    <div className='error-messages-section'>
                        <div className={cx('error-message')}>
                            <span className='paragraph-small error_overview'>
                                <span className='paragraph-small--bold'>Samantekt: </span>
                                {hasErrors.length} villur fundust - vinsamlegast lagið allar villur til að senda skjal
                            </span>
                            <div className={cx('error_details_container', { 'with_scroll': hasErrors.length > MIN_VISIBLE_ERRORS && visibleErrorCount > MIN_VISIBLE_ERRORS })}>
                                {/* Show gradient overlay when there are hidden errors */}
                                {visibleErrorCount === MIN_VISIBLE_ERRORS && <div className='gradient_overlay' />}

                                {/* Display errors up to the current visible count */}
                                {hasErrors.slice(0, visibleErrorCount).map((error, index) => {
                                    if (!error.error || error.error.length === 0) {
                                        return null;
                                    }
                                    return (
                                        <div key={index} className='error_row_container'>
                                            <p className='error_rowNumber'>
                                                Röð {error.error[0]?.row - 1}:
                                            </p>
                                            <div className='error_messages_container'>
                                                {error.error.map((err, i) => (
                                                    <div key={i}>
                                                        <span className='error_field'>{err.field}</span>
                                                        <span className='error_message'>
                                                            - {err.message}{error.error && i < error.error.length - 1 ? ', ' : ''}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Toggle button to expand/collapse error list */}
                        {hasErrors.length > MIN_VISIBLE_ERRORS && (
                            <span
                                className='paragraph-xsmall showAllText'
                                onClick={() => {
                                    if (visibleErrorCount > MIN_VISIBLE_ERRORS) {
                                        setVisibleErrorCount(MIN_VISIBLE_ERRORS);
                                    } else {
                                        const nextBatch = Math.min(visibleErrorCount + 100, hasErrors.length);
                                        setVisibleErrorCount(nextBatch);
                                    }
                                }}
                            >
                                {visibleErrorCount > MIN_VISIBLE_ERRORS
                                    ? 'Sýna minna'
                                    : (() => {
                                        const remainingErrors = hasErrors.length - visibleErrorCount;
                                        const nextBatchSize = Math.min(remainingErrors, 100);
                                        return `Birta ${nextBatchSize} villur í viðbót`;
                                    })()
                                }
                            </span>
                        )}
                    </div>
                }

                {/* Action buttons for canceling or submitting the upload */}
                <div className='action-button-group' style={{ marginTop: '20px' }}>
                    <Button buttonType='secondary' onClick={cancelUpload}>Hætta við</Button>
                    <Button
                        disabled={uploadedEntities.length === 0 || hasErrors.length > 0}
                        buttonType='primary'
                        onClick={handleSubmitUploadedEntities}
                    >
                        Senda
                    </Button>
                </div>
            </div>
        }

        // Main render: show upload section and either entity table or single-entry form
        return (
            <div className="KCL_add-customer-modal">
                <div className='upload-section'>
                    <p className="paragraph excel-instructions">
                        Hægt er að bæta við einstaklingum úr Excel skjali, sjá <Link icon='excel' url="/templates/add_individual_template.xlsx">Sniðmát í Excel</Link>
                    </p>
                    <FileUploadComponent
                        setEntities={setUploadedEntities}
                        setErrors={setErrors}
                        onProcessingStart={onProcessingStart}
                        onProgressUpdate={onProgressUpdate}
                        onProcessingComplete={onProcessingComplete}
                        onProcessingAborted={onProcessingAborted}
                        setUploadedFile={setUploadedFile}
                        abortRef={abortRef}
                        validationType='individual'
                    />
                </div>

                {/* Show uploaded entities table after parsing completes (if no file errors) */}
                {parsingState === "completed" && !FILE_ERROR && showUploadedEntities()}

                {/* Show file-level error if present */}
                {FILE_ERROR ?
                    <div className='error-message'>
                        <Alert type={'error'} headText={FILE_ERROR.headerMessage ?? 'Villa'} text={FILE_ERROR.message} />
                        <div className='action-button-group' style={{ marginTop: '20px' }}>
                            <Button buttonType='secondary' onClick={cancelUpload}>Hætta við</Button>
                        </div>
                    </div>
                    : uploadedEntities.length > 0 ? null
                        // Show single-entry form if no bulk upload in progress
                        : <>
                            <h6>Upplýsingar um einstakling</h6>
                            <Formik
                                initialValues={{
                                    foreign: false,
                                    ssn: '',
                                    name: '',
                                    email: '',
                                    address: '',
                                    areaCode: '',
                                    region: '',
                                    country: '',
                                    nationality: '',
                                    supervisorId: 0,
                                    dateOfBirth: null
                                }}
                                validate={validate}
                                onSubmit={handleSubmit}
                                component={subFormComponent}
                                validateOnMount={false}
                                validateOnBlur={true}
                                validateOnChange={true}
                            />
                        </>
                }
            </div>
        );
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            header="Bæta við einstakling"
            body={renderBody()}
            hasCloseButton
        />
    );
};

export default AddIndividualModal;